import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { mortgageApi } from '../workspacesApi';
import { uc } from '../unifiedContactsApi';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';
import { OwnerSelect, useStaff } from '../kit/OwnerChip';
import OppContactField from './OppContactField';
import { MORTGAGE_FIELDS, MORTGAGE_CLOSED_STAGES, buildForm, createPayload, OppFields } from './oppFields';

const blankForm = () => ({ ...buildForm(MORTGAGE_FIELDS), loan_purpose: 'purchase', loan_type: 'conventional' });
const NO_OFFICER = '__none__';
const LABEL = 'text-sm font-medium leading-none';

// Loan officer picker; its own component so the staff roster is only fetched when the caller may assign.
function OfficerField({ value, onChange }) {
  const staff = useStaff();
  return (
    <div>
      <Label>Loan Officer</Label>
      <OwnerSelect value={value || NO_OFFICER} staff={staff} extra={[[NO_OFFICER, 'Unassigned']]}
        onChange={(v) => onChange(v === NO_OFFICER ? '' : v)} placeholder="Unassigned" testId="opp-loan-officer" className="mt-1" />
    </div>
  );
}

// Create-opportunity modal (from the retired Mortgage Workspace page). The contact comes from the
// server-side contact search; onCreated(opp, contact) lets the Board append the card without a
// refetch. `stages` = the board's columns (terminal stages are not offered), `lenders` = the
// directory; the loan-officer field shows only when `canAssign`. `initialContact` starts the dialog
// with that contact picked (the contact profile's "New opportunity" link, ticket 188); the Board keys
// the dialog on it, so it is read once per mount.
export default function CreateOpportunityDialog({ open, onOpenChange, onCreated, stages = [], lenders = [], canAssign = false, initialContact = null }) {
  const [contact, setContact] = useState(initialContact);
  const [form, setForm] = useState(blankForm);
  const [stage, setStage] = useState('');
  const [officer, setOfficer] = useState('');
  const [prefilled, setPrefilled] = useState(null);   // the FICO copied from the contact record
  const [saving, setSaving] = useState(false);

  const stageOptions = useMemo(
    () => stages.filter(s => !MORTGAGE_CLOSED_STAGES.includes(s.key)).map(s => ({ value: s.key, label: s.label })),
    [stages],
  );
  const stageValue = stageOptions.some(o => o.value === stage)
    ? stage
    : (stageOptions.find(o => o.value === 'new_lead') || stageOptions[0])?.value;

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Ticket 75: picking a contact prefills the credit score from the contact's latest known FICO
  // (the search result does not carry it, so read the record); the field stays editable.
  const pickContact = async (c) => {
    setContact(c);
    setPrefilled(null);
    if (!c) return;
    try {
      const full = await uc.get(c.contact_id);
      if (full?.credit_score) {
        setPrefilled(full.credit_score);
        setField('credit_score', full.credit_score);
      }
    } catch { /* no prefill; the score stays whatever was typed */ }
  };
  const seeded = useRef(false);
  useEffect(() => {
    if (!initialContact || seeded.current) return;
    seeded.current = true;
    pickContact(initialContact);
  });
  const scoreFromContact = prefilled != null && String(prefilled) === String(form.credit_score ?? '');

  const create = async () => {
    if (!contact) { toast({ title: 'Select a contact' }); return; }
    setSaving(true);
    try {
      const opp = await mortgageApi.createOpp({
        contact_id: contact.contact_id,
        ...createPayload(MORTGAGE_FIELDS, form),
        ...(stageValue ? { stage: stageValue } : {}),
        ...(canAssign && officer ? { loan_officer_id: officer } : {}),
      });
      toast({ title: 'Opportunity created' });
      onCreated?.(opp, contact);
      setForm(blankForm());
      setStage('');
      setOfficer('');
      setPrefilled(null);
      setContact(null);
      onOpenChange(false);
    } catch (e) {
      toast({ title: 'Create failed', description: errorMessage(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pipeline-create-dialog">
        <DialogHeader><DialogTitle className="font-serif" style={{ color: '#070B5F' }}>New Mortgage Opportunity</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <OppContactField contact={contact} onPick={pickContact} />
          <div className="grid grid-cols-2 gap-3">
            {stageOptions.length > 0 && (
              <PickSelect label="Stage" labelClassName={LABEL} value={stageValue} onChange={setStage} options={stageOptions} testId="opp-stage-select" />
            )}
            {canAssign && <OfficerField value={officer} onChange={setOfficer} />}
          </div>
          <OppFields fields={MORTGAGE_FIELDS} form={form} setField={setField} lenders={lenders} testPrefix="opp"
            hints={{ credit_score: scoreFromContact ? <div className="text-[11px] text-slate-400 mt-1" data-testid="opp-credit-score-source">From contact record</div> : null }} />
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-outline" onClick={() => onOpenChange(false)}>Cancel</button>
            <button disabled={saving} onClick={create} data-testid="opp-save-btn" className="btn-gold">
              {saving && <Loader2 className="animate-spin" size={14} />} Create
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
